/* eslint-disable @next/next/no-img-element */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {useEffect, useState} from 'react';
import Link from 'next/link';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  Divider,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import {footerCityJobsData, socialData, socialLinks} from './constants';
import {ABS_URL} from './env';
import {openUrl, deferTask} from '@jobseeker/common-core/utils';

const useScreenSize = () => {
  const isMobile = useMediaQuery('(max-width: 450px)');
  const isSmall = useMediaQuery('(min-width: 451px) and (max-width: 1100px)');

  return {isMobile, isSmall};
};

const Dot = () => (
  <Box
    component="span"
    sx={{
      width: 4,
      height: 4,
      bgcolor: '#cecece',
      borderRadius: '50%',
      display: 'inline-block',
      mx: '1.75px',
      verticalAlign: 'middle',
    }}
  />
);

const Dot2 = () => (
  <Box
    component="span"
    sx={{
      width: 4,
      height: 4,
      bgcolor: 'black',
      opacity: 0.5,
      borderRadius: '50%',
      display: 'inline-block',
      mx: '1.75px',
      mr: 1,
      verticalAlign: 'middle',
    }}
  />
);

const CityPageList = ({colCount, heading, data}: any) => {
  const listData = [];
  for (let i = 0; i < data.length; i += colCount) {
    listData.push(data.slice(i, i + colCount));
  }

  return (
    <Box sx={{margin: 1, mt: 2, width: '100%'}} data-testid="jobs-page-list">
      <Typography
        component={'h2'}
        sx={{fontSize: 18, fontWeight: 700, fontFamily: 'Nunito Sans'}}
      >
        {heading}
      </Typography>
      <Grid container sx={{margin: 0}}>
        {listData.map((chunkList, index) => (
          <>
            <Grid item xxs={heading == 'Jobs by location' ? 6 : 12} key={index}>
              <List>
                {chunkList.map((city: any, idx: number) => (
                  <ListItem
                    key={idx}
                    style={{
                      paddingLeft: '0px !important',
                      paddingRight: '0px !important',
                      paddingTop: '6px',
                      paddingBottom: 0,
                    }}
                    disableGutters
                  >
                    <Link
                      href={city.link}
                      passHref
                      style={{
                        textDecoration: 'none',
                        margin: 0,
                      }}
                      target="_blank"
                      onClick={e => {
                        e.preventDefault();
                        const url = city.link;
                        deferTask(() => openUrl(url, '_blank'));
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                          paddingLeft: '0px !important',
                          paddingRight: '0px !important',
                          wordBreak: 'break-word',
                          width: 'fit-content',
                          maxWidth: '100%',
                        }}
                      >
                        <Dot2 />
                        <Typography
                          sx={{
                            fontFamily: '"Nunito Sans", sans-serif !important',
                            fontSize: {
                              xxs: '13px !important',
                              md: '16px !important',
                            },
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            color: 'rgba(0, 0, 0, 0.8)',
                            opacity: 0.6,
                            fontWeight: 400,
                            paddingLeft: '0px',
                            paddingRight: '0px',
                          }}
                        >
                          {city.text}
                        </Typography>
                      </Box>
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </>
        ))}
      </Grid>
    </Box>
  );
};

interface HomePageFooterProps {
  isTransparent?: boolean;
}

const HomePageFooter: React.FC<HomePageFooterProps> = ({
  isTransparent = false,
}) => {
  const {isMobile, isSmall} = useScreenSize();

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: isTransparent ? 'transparent' : 'background.paper',
      }}
    >
      <Grid container>
        <Grid item xxs={12} xl={2.5} lg={3} md={3.5}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: '40px 60px 25px',
              paddingLeft: isMobile ? '16px' : '60px',
            }}
          >
            <Box display="flex" sx={{alignItems: 'center'}}>
              {socialData.map((data, index) => (
                <Link
                  href={data.link}
                  key={index}
                  rel="nofollow"
                  passHref
                  style={{marginLeft: '10px'}}
                  onClick={e => {
                    e.preventDefault();
                    const url = data.link;
                    deferTask(() => openUrl(url, '_self'));
                  }}
                >
                  <Image
                    src={data.img.src as unknown as string}
                    alt={data.img.alt}
                    width={
                      data.img.width == 60
                        ? isSmall
                          ? 40
                          : 60
                        : data.img.width
                    }
                    height={
                      data.img.height == 60
                        ? isSmall
                          ? 40
                          : 60
                        : data.img.height
                    }
                  />
                </Link>
              ))}
            </Box>
            <Typography
              sx={{
                mt: 4,
                fontFamily: 'Nunito Sans',
                fontSize: 18,
                fontWeight: 700,
                marginLeft: '10px',
              }}
            >
              Follow Us
            </Typography>
            <Box sx={{display: 'flex', gap: 1.5, ml: 1.25, mt: 1}}>
              {socialLinks.map((social, index) => (
                <IconButton
                  style={{padding: 0}}
                  href={social.link}
                  key={index}
                  target="_blank"
                  onClick={e => {
                    e.preventDefault();
                    const url = social.link;
                    deferTask(() => openUrl(url, '_blank'));
                  }}
                >
                  <Image
                    src={social.src as unknown as string}
                    alt={social.alt}
                    width={34}
                    height={34}
                  />
                </IconButton>
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xxs={12}
          md={8.5}
          lg={9}
          xl={9.5}
          sx={{
            overflowX: 'hidden',
            maxWidth: '100vw',
          }}
        >
          <Box
            display={{md: 'flex'}}
            width={{xxs: '90%', md: '97%'}}
            sx={{overflowY: 'auto', overflowX: 'hidden'}}
            marginTop={{xxs: 0, md: 3}}
            marginLeft={{xxs: '16px', md: 4}}
            mb={{xxs: 0, md: 2.25}}
          >
            {footerCityJobsData.map((data, index) => (
              <CityPageList
                key={`footer_key_${index}`}
                {...data}
                data-testid={`footer-job-item-${index}`}
              />
            ))}
          </Box>
        </Grid>
      </Grid>
      <Divider
        sx={{marginTop: 0, marginBottom: '0px !important', paddingBottom: 0}}
      />
      <Box
        sx={{
          display: {md: 'flex', xxs: 'grid'},
          gridTemplateColumns: {xxs: '1fr 1fr', md: 'auto'},
          justifyContent: {xxs: 'center', md: 'center'},
          flexWrap: {xxs: 'wrap', md: 'nowrap'}, // Wrap on small screens, stay in row for md+
          p: {xxs: 2.5, md: 3},
          pl: {xxs: 6, md: 2},
          gap: {xxs: 0, md: 2},
        }}
      >
        {[
          {text: 'Terms of Service', href: '/terms'},
          {text: 'Privacy Policy', href: '/privacy'},
          {text: 'Contact Us', href: '/contact'},
          {text: 'Blog', href: 'https://www.hirist.tech/blog'},
          {text: 'Sitemap', href: `${ABS_URL}/sitemap`},
          {text: 'Grievances', href: 'https://w5.naukri.com/grievances-form'},
        ].map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: {xxs: 1.25, md: 0},
              width: {xxs: '100%', md: 'auto'}, // 50% width on small screens, auto on medium+
            }}
          >
            {isMobile && <Dot />}
            <Link
              style={{
                textDecoration: 'none',
                fontFamily: 'Nunito Sans !important',
                fontSize: 16,
                color: 'rgba(0,0,0,.8)',
                opacity: 0.7,
                fontWeight: 400,
              }}
              href={item.href}
              target="_blank"
              rel="nofollow"
              onClick={e => {
                e.preventDefault();
                const url = item.href;
                deferTask(() => openUrl(url, '_blank'));
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Nunito Sans !important',
                  marginLeft: {xxs: 1, md: 0},
                  marginRight: {xxs: '8px', md: '16px'},
                }}
              >
                {item.text}
              </Typography>
            </Link>
            {index < 5 && !isMobile && <Dot />} {/* Hide last Dot */}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HomePageFooter;
